/**
 * API Service for backend API calls
 */

// API base URL - adjust this based on your environment
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Get auth token from session storage
const getAuthToken = () => {
  return sessionStorage.getItem('token');
};

/**
 * Get the last sync time from the API
 * @returns {Promise<string>} - Promise that resolves to formatted last sync time
 */
export const getLastSyncTime = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/alumni/sync-status`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`
      }
    });
    
    if (!response.ok) {
      return 'Never';
    }
    
    const data = await response.json();
    if (!data.lastSync) return 'Never';
    
    const date = new Date(data.lastSync);
    return date.toLocaleString();
  } catch (error) {
    console.error('Error getting last sync time:', error);
    return 'Never';
  }
};

/**
 * Update the last sync time in the API
 */
export const updateLastSyncTime = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/alumni/sync-status`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`
      },
      body: JSON.stringify({
        lastSync: new Date().toISOString()
      })
    });
    
    if (!response.ok) {
      throw new Error('Failed to update sync time');
    }
    
    return getLastSyncTime();
  } catch (error) {
    console.error('Error updating sync time:', error);
    throw error;
  }
};

/**
 * Call OpenAI API to search for LinkedIn information
 * @param {Object} alumnus - Alumni data
 * @returns {Promise<Object>} - Promise that resolves to LinkedIn data
 */
/**
 * Batch search for LinkedIn information for multiple alumni
 * @param {Array} alumniList - List of alumni to search for
 * @param {Function} onProgress - Callback for progress updates
 * @param {Function} onComplete - Callback for completion
 * @returns {Promise} - Promise that resolves when all searches are complete
 */
export const batchSearchLinkedIn = async (alumniList, onProgress, onComplete) => {
  try {
    console.log(`Starting batch LinkedIn search for ${alumniList.length} alumni`);
    
    // Initialize progress tracking
    let completed = 0;
    const total = alumniList.length;
    const results = [];
    
    // Initial progress update
    if (onProgress) {
      onProgress({
        completed,
        total,
        errors: 0
      });
    }
    
    // Process alumni in smaller batches to avoid overwhelming the server
    const BATCH_SIZE = 5; // Process 5 at a time
    
    for (let i = 0; i < alumniList.length; i += BATCH_SIZE) {
      const batch = alumniList.slice(i, i + BATCH_SIZE);
      const batchPromises = batch.map(async (alumnus) => {
        try {
          console.log(`Searching LinkedIn for ${alumnus.emp_empname}`);
          
          // Call the single search API for each alumnus
          const linkedInData = await searchLinkedInWithOpenAI(alumnus);
          
          // Update progress
          completed++;
          
          // Store the result
          const result = {
            alumniId: alumnus._id || alumnus.emp_empcode,
            found: linkedInData.found,
            linkedInUrl: linkedInData.profileUrl || '',
            currentTitle: linkedInData.currentPosition?.title || 'Not available',
            currentCompany: linkedInData.currentPosition?.company || 'Not available',
            reasoning: linkedInData.reasoning || ''
          };
          
          results.push(result);
          
          // Call progress callback
          if (onProgress) {
            onProgress({
              completed,
              total,
              alumniId: alumnus._id || alumnus.emp_empcode,
              result
            });
          }
          
          return result;
        } catch (error) {
          console.error(`Error searching LinkedIn for ${alumnus.emp_empname}:`, error);
          
          // Still increment completed count
          completed++;
          
          // Call progress callback with error
          if (onProgress) {
            onProgress({
              completed,
              total,
              alumniId: alumnus._id || alumnus.emp_empcode,
              error: error.message
            });
          }
          
          return {
            alumniId: alumnus._id || alumnus.emp_empcode,
            error: error.message,
            found: false
          };
        }
      });
      
      // Wait for all promises in this batch to resolve
      await Promise.all(batchPromises);
      
      // Small delay between batches to avoid rate limiting
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    
    // Call complete callback when all are done
    if (onComplete) {
      onComplete(results);
    }
    
    return results;
  } catch (error) {
    console.error('Error in batch search:', error);
    
    if (onComplete) {
      onComplete({
        error: error.message,
        completed: 0,
        total: alumniList.length
      });
    }
    
    throw error;
  }
};

/**
 * Call backend API to search for LinkedIn information
 * @param {Object} alumnus - Alumni data
 * @returns {Promise<Object>} - Promise that resolves to LinkedIn data
 */
export const searchLinkedInWithOpenAI = async (alumnus) => {
  try {
    if (!alumnus) {
      throw new Error('No alumni data provided');
    }
    
    console.log('Calling LinkedIn search API for:', alumnus.emp_empname);
    
    // Create an AbortController for timeout handling
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 35000); // 35 second timeout
    
    try {
      // Call the backend API with timeout
      const response = await fetch(`${API_BASE_URL}/linkedin/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`
        },
        body: JSON.stringify({
          // Send either the MongoDB ID or the employee code
          alumniId: alumnus._id || null,
          empCode: alumnus.emp_empcode || null,
          name: alumnus.emp_empname || '',
          email: alumnus.emp_email || ''
        }),
        signal: controller.signal
      });
      
      // Clear the timeout since we got a response
      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          error: `HTTP error ${response.status}: ${response.statusText}`
        }));
        throw new Error(`API Error: ${errorData.error || 'Unknown error'}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to search LinkedIn');
      }
      
      console.log('LinkedIn search API returned data successfully');
      return data.data;
    } catch (fetchError) {
      // Clear the timeout to prevent memory leaks
      clearTimeout(timeoutId);
      
      // Handle abort errors specifically
      if (fetchError.name === 'AbortError') {
        throw new Error('API request timed out');
      }
      
      throw fetchError;
    }
  } catch (error) {
    console.error('Error calling LinkedIn search API:', error);
    throw error;
  }
};

/**
 * Get all campaigns
 * @returns {Promise<Array>} - Promise that resolves to an array of campaigns
 */
export const getCampaigns = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/campaigns`, {
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`
      }
    });
    if (!response.ok) {
      throw new Error('Failed to fetch campaigns');
    }
    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error fetching campaigns:', error);
    throw error;
  }
};

/**
 * Get a specific campaign by ID
 * @param {string} id - Campaign ID
 * @returns {Promise<Object>} - Promise that resolves to campaign data
 */
export const getCampaignById = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/campaigns/${id}`, {
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`
      }
    });
    if (!response.ok) {
      throw new Error('Failed to fetch campaign');
    }
    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error fetching campaign:', error);
    throw error;
  }
};

/**
 * Create a new campaign
 * @param {Object} campaign - Campaign data
 * @returns {Promise<Object>} - Promise that resolves to the created campaign
 */
export const createCampaign = async (campaign) => {
  try {
    const response = await fetch(`${API_BASE_URL}/campaigns`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`
      },
      body: JSON.stringify(campaign)
    });
    if (!response.ok) {
      throw new Error('Failed to create campaign');
    }
    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error creating campaign:', error);
    throw error;
  }
};

/**
 * Update a campaign
 * @param {string} id - Campaign ID
 * @param {Object} campaign - Updated campaign data
 * @returns {Promise<Object>} - Promise that resolves to the updated campaign
 */
export const updateCampaign = async (id, campaign) => {
  try {
    const response = await fetch(`${API_BASE_URL}/campaigns/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`
      },
      body: JSON.stringify(campaign)
    });
    if (!response.ok) {
      throw new Error('Failed to update campaign');
    }
    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error updating campaign:', error);
    throw error;
  }
};

/**
 * Delete a campaign
 * @param {string} id - Campaign ID
 * @returns {Promise<void>} - Promise that resolves when the campaign is deleted
 */
export const deleteCampaign = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/campaigns/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`
      }
    });
    if (!response.ok) {
      throw new Error('Failed to delete campaign');
    }
  } catch (error) {
    console.error('Error deleting campaign:', error);
    throw error;
  }
};

/**
 * Get all alumni
 * @returns {Promise<Array>} - Promise that resolves to an array of alumni
 */
export const getAlumni = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/alumni`, {
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`
      }
    });
    if (!response.ok) {
      throw new Error('Failed to fetch alumni');
    }
    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error fetching alumni:', error);
    throw error;
  }
};

/**
 * Create a new alumnus
 * @param {Object} alumnus - Alumni data
 * @returns {Promise<Object>} - Promise that resolves to the created alumnus
 */
export const createAlumni = async (alumnus) => {
  try {
    const response = await fetch(`${API_BASE_URL}/alumni`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`
      },
      body: JSON.stringify(alumnus)
    });
    if (!response.ok) {
      throw new Error('Failed to create alumnus');
    }
    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error creating alumnus:', error);
    throw error;
  }
};

/**
 * Update an alumnus
 * @param {string} id - Alumni ID
 * @param {Object} alumnus - Updated alumni data
 * @returns {Promise<Object>} - Promise that resolves to the updated alumnus
 */
export const updateAlumni = async (id, alumnus) => {
  try {
    const response = await fetch(`${API_BASE_URL}/alumni/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`
      },
      body: JSON.stringify(alumnus)
    });
    if (!response.ok) {
      throw new Error('Failed to update alumnus');
    }
    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error updating alumnus:', error);
    throw error;
  }
};
/**
 * Delete an alumnus
 * @param {string} id - Alumni ID
 * @returns {Promise<void>} - Promise that resolves when the alumnus is deleted
 */
export const deleteAlumni = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/alumni/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`
      }
    });
    if (!response.ok) {
      throw new Error('Failed to delete alumnus');
    }
  } catch (error) {
    console.error('Error deleting alumnus:', error);
    throw error;
  }
};

/**
 * Get campaign contacts
 * @param {string} campaignId - Campaign ID
 * @returns {Promise<Array>} - Promise that resolves to an array of campaign contacts
 */
export const getCampaignContacts = async (campaignId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/campaigns/${campaignId}/contacts`, {
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`
      }
    });
    if (!response.ok) {
      throw new Error('Failed to fetch campaign contacts');
    }
    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error fetching campaign contacts:', error);
    throw error;
  }
};

/**
 * Add a contact to a campaign
 * @param {string} campaignId - Campaign ID
 * @param {Object} contact - Contact data
 * @returns {Promise<Object>} - Promise that resolves to the added contact
 */
export const addCampaignContact = async (campaignId, contact) => {
  try {
    console.log('Adding contact to campaign:', { campaignId, contact });
    const response = await fetch(`${API_BASE_URL}/campaigns/${campaignId}/contacts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`
      },
      body: JSON.stringify(contact)
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      console.error('Server response:', data);
      throw new Error(data.error || 'Failed to add campaign contact');
    }
    
    if (!data.success) {
      throw new Error(data.error || 'Failed to add campaign contact');
    }
    
    return data;
  } catch (error) {
    console.error('Error adding campaign contact:', error);
    throw error;
  }
};

/**
 * Update a campaign contact
 * @param {string} campaignId - Campaign ID
 * @param {string} contactId - Contact ID
 * @param {Object} contact - Updated contact data
 * @returns {Promise<Object>} - Promise that resolves to the updated contact
 */
export const updateCampaignContact = async (campaignId, contactId, contact) => {
  try {
    const response = await fetch(`${API_BASE_URL}/campaigns/${campaignId}/contacts/${contactId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`
      },
      body: JSON.stringify(contact)
    });
    if (!response.ok) {
      throw new Error('Failed to update campaign contact');
    }
    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error updating campaign contact:', error);
    throw error;
  }
};

/**
 * Remove a contact from a campaign
 * @param {string} campaignId - Campaign ID
 * @param {string} contactId - Contact ID
 * @returns {Promise<void>} - Promise that resolves when the contact is removed
 */
export const removeCampaignContact = async (campaignId, contactId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/campaigns/${campaignId}/contacts/${contactId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`
      }
    });
    
    const data = await response.json();
    
    if (!response.ok || !data.success) {
      throw new Error(data.error || 'Failed to remove campaign contact');
    }
    
    return data;
  } catch (error) {
    console.error('Error removing campaign contact:', error);
    throw error;
  }
};

/**
 * Get all gifts
 * @returns {Promise<Array>} - Promise that resolves to an array of gifts
 */
export const getGifts = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/gifts`, {
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`
      }
    });
    if (!response.ok) {
      throw new Error('Failed to fetch gifts');
    }
    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error fetching gifts:', error);
    throw error;
  }
};

/**
 * Create a new gift
 * @param {Object} gift - Gift data
 * @returns {Promise<Object>} - Promise that resolves to the created gift
 */
export const createGift = async (gift) => {
  try {
    const response = await fetch(`${API_BASE_URL}/gifts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`
      },
      body: JSON.stringify(gift)
    });
    if (!response.ok) {
      throw new Error('Failed to create gift');
    }
    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error creating gift:', error);
    throw error;
  }
};

/**
 * Update a gift
 * @param {string} id - Gift ID
 * @param {Object} gift - Updated gift data
 * @returns {Promise<Object>} - Promise that resolves to the updated gift
 */
export const updateGift = async (id, gift) => {
  try {
    const response = await fetch(`${API_BASE_URL}/gifts/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`
      },
      body: JSON.stringify(gift)
    });
    if (!response.ok) {
      throw new Error('Failed to update gift');
    }
    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error updating gift:', error);
    throw error;
  }
};

/**
 * Delete a gift
 * @param {string} id - Gift ID
 * @returns {Promise<void>} - Promise that resolves when the gift is deleted
 */
export const deleteGift = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/gifts/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`
      }
    });
    if (!response.ok) {
      throw new Error('Failed to delete gift');
    }
  } catch (error) {
    console.error('Error deleting gift:', error);
    throw error;
  }
};

/**
 * Register a new user
 * @param {Object} userData - User registration data (name, email, password)
 * @returns {Promise<Object>} - Promise that resolves to user data
 */
export const register = async (userData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(userData)
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Registration failed');
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Registration error:', error);
    throw error;
  }
};

/**
 * Login user
 * @param {Object} credentials - User credentials (email, password)
 * @returns {Promise<Object>} - Promise that resolves to user data and token
 */
export const login = async (credentials) => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(credentials)
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Login failed');
    }
    
    const data = await response.json();
    
    // Store the token
    sessionStorage.setItem('token', data.token);
    
    return data;
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

/**
 * Logout user
 */
export const logout = () => {
  sessionStorage.removeItem('token');
};

/**
 * Check if user is authenticated
 * @returns {Promise<boolean>}
 */
export const isAuthenticated = async () => {
  const token = sessionStorage.getItem('token');
  if (!token) return false;

  // Check if it's an admin token
  if (token.startsWith('admin-token-')) {
    return true;
  }

  try {
    const response = await fetch(`${API_BASE_URL}/auth/me`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.ok;
  } catch (error) {
    console.error('Auth verification error:', error);
    return false;
  }
};

/**
 * Import alumni data in bulk
 * @param {Array} alumni - Array of alumni objects to import
 * @returns {Promise<Object>} - Promise that resolves to the import results
 */
export const importAlumni = async (alumni) => {
  try {
    const response = await fetch(`${API_BASE_URL}/alumni/import`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`
      },
      body: JSON.stringify({ alumni })
    });

    if (!response.ok) {
      throw new Error('Failed to import alumni data');
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error importing alumni:', error);
    throw error;
  }
};

export const updateCampaignStatus = async (campaignId, newStatus) => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('No authentication token found');
    }

    // Use the existing updateCampaign function with just the status field
    return await updateCampaign(campaignId, { status: newStatus });
  } catch (error) {
    console.error('Error updating campaign status:', error);
    throw error;
  }
};

export const sendGift = async (giftData) => {
  const token = sessionStorage.getItem('token');
  if (!token) {
    throw new Error('Authentication required');
  }

  try {
    const response = await fetch(`${API_BASE_URL}/gifts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        campaign: giftData.campaignId,
        alumni: giftData.alumniId,
        giftType: giftData.giftType,
        customMessage: giftData.message || '',
        budget: giftData.budget || 50, // Default budget of $50
        scheduledDate: new Date().toISOString(), // Schedule for immediate delivery
        status: 'scheduled'
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to send gift');
    }

    return await response.json();
  } catch (error) {
    console.error('Error sending gift:', error);
    throw error;
  }
};

